import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import Button from "../components/Button";
import LeaderboardTable from "../components/LeaderboardTable";
import PageShell from "../components/PageShell";
import StatusBanner from "../components/StatusBanner";
import { calculateFinalScore } from "../lib/scoringUtils";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const views = {
  default: "default",
  overall: "overall",
};

export default function Leaderboard() {
  const [activeView, setActiveView] = useState(views.default);
  const [rows, setRows] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLeaderboard(activeView);
  }, [activeView]);

  async function loadLeaderboard(view) {
    setIsLoading(true);
    setError(null);

    try {
      if (!isSupabaseConfigured) {
        setSession(null);
        setCycle(null);
        setRows([]);
        setError(
          "Supabase is not configured yet. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env to load snapshots.",
        );
        return;
      }

      // Fetch all data from the secure view — works for anon + authenticated.
      // The view already limits to Top 75 per session+cycle, joins mentor names, and provides rank.
      // We always fetch everything and filter/aggregate client-side.
      const { data, error: viewError } = await supabase
        .from("public_leaderboard_view")
        .select("*")
        .order("rank", { ascending: true });

      if (viewError) throw viewError;

      if (!data || !data.length) {
        setRows([]);
        setSession(null);
        setCycle(null);
        setError(null);
        return;
      }

      // Filter to ONLY the current academic session
      const currentSessionRows = data.filter((r) => r.session_is_current);
      if (!currentSessionRows.length) {
        setSession(null);
        setCycle(null);
        setRows([]);
        setError(null);
        return;
      }

      setSession({ session_name: currentSessionRows[0].session_name });

      // Only consider closed cycles
      const now = new Date();
      const closedRows = currentSessionRows.filter(
        (r) => r.cycle_id && r.cycle_closes_at && new Date(r.cycle_closes_at) <= now,
      );

      if (!closedRows.length) {
        setCycle(null);
        setRows([]);
        setError(null);
        return;
      }

      if (view !== views.overall) {
        // Default view: show the last closed cycle's ranking
        closedRows.sort(
          (a, b) => new Date(b.cycle_closes_at) - new Date(a.cycle_closes_at),
        );
        const latestClosedCycleId = closedRows[0].cycle_id;
        const cycleRows = closedRows
          .filter((r) => r.cycle_id === latestClosedCycleId)
          .sort((a, b) => a.rank - b.rank);
        setCycle({ cycle_name: cycleRows[0]?.cycle_name ?? "Latest cycle" });
        setRows(cycleRows);
      } else {
        // Overall session view: aggregate across all closed cycles per mentor
        setCycle(null);

        // Group by mentor
        const mentorMap = new Map();
        closedRows.forEach((r) => {
          if (!mentorMap.has(r.mentor_id)) {
            mentorMap.set(r.mentor_id, {
              id: r.id,
              mentor_id: r.mentor_id,
              session_id: r.session_id,
              mentor_name: r.mentor_name,
              mentor_roll_number: r.mentor_roll_number,
              session_name: r.session_name,
              reviewSum: 0,
              engagementSum: 0,
              cycleCount: 0,
            });
          }
          const entry = mentorMap.get(r.mentor_id);
          entry.reviewSum += r.review;
          entry.engagementSum += r.engagement_score;
          entry.cycleCount += 1;
        });

        // Compute averages and final scores
        const aggregated = Array.from(mentorMap.values()).map((entry) => {
          const avgReview = entry.cycleCount > 0 ? entry.reviewSum / entry.cycleCount : 0;
          const avgEngagement = entry.cycleCount > 0 ? entry.engagementSum / entry.cycleCount : 0;
          const finalScore = calculateFinalScore({
            averageRating: avgReview,
            engagementScore: avgEngagement,
          });
          return {
            id: entry.id,
            mentor_id: entry.mentor_id,
            session_id: entry.session_id,
            mentor_name: entry.mentor_name,
            mentor_roll_number: entry.mentor_roll_number,
            session_name: entry.session_name,
            review: avgReview,
            engagement_score: avgEngagement,
            final_score: finalScore,
          };
        });

        // Sort by final_score descending, assign rank, take top 75
        aggregated.sort((a, b) => b.final_score - a.final_score);
        const ranked = aggregated.slice(0, 75).map((row, i) => ({
          ...row,
          rank: i + 1,
        }));

        setRows(ranked);
      }
    } catch (caughtError) {
      setRows([]);
      setError(caughtError.message ?? "Could not load the leaderboard.");
    } finally {
      setIsLoading(false);
    }
  }

  /* ---- style helpers ---- */
  const card = {
    borderRadius: "16px",
    border: "1px solid #dfe8ee",
    background: "#fff",
    padding: "16px",
    boxShadow: "0 10px 26px rgba(36,55,70,0.06)",
  };
  const statLabel = {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    color: "#64748b",
  };
  const toggleBase = {
    border: "none",
    background: "transparent",
    minHeight: "36px",
    borderRadius: "8px",
    padding: "0 16px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontFamily: "inherit",
  };

  return (
    <PageShell
      actions={
        <>
          <div
            style={{
              display: "inline-flex",
              borderRadius: "8px",
              border: "1px solid #dfe8ee",
              background: "#fff",
              padding: "4px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            }}
          >
            <button
              style={{
                ...toggleBase,
                ...(activeView === views.default
                  ? { background: "#d9f3f2", color: "#028D93", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }
                  : { color: "#475569" }),
              }}
              onClick={() => setActiveView(views.default)}
              type="button"
            >
              Latest Cycle
            </button>
            <button
              style={{
                ...toggleBase,
                ...(activeView === views.overall
                  ? { background: "#d9f3f2", color: "#028D93", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }
                  : { color: "#475569" }),
              }}
              onClick={() => setActiveView(views.overall)}
              type="button"
            >
              Overall Session
            </button>
          </div>
          <Button
            icon={RefreshCw}
            onClick={() => loadLeaderboard(activeView)}
            variant="secondary"
          >
            Refresh
          </Button>
        </>
      }
      eyebrow="Student Welfare Group"
      description="Top performing mentors based on mentee reviews and engagement."
      showSignOut={false}
      title="Mentor Leaderboard"
    >
      {rows.length > 0 && (
        <div className="mentorship-stats-grid" style={{ marginBottom: "20px" }}>
          <div style={card}>
            <p style={statLabel}>Session</p>
            <p style={{ marginTop: "8px", fontWeight: 600, color: "#00293A" }}>
              {session?.session_name ?? "Current Session"}
            </p>
          </div>
          <div style={card}>
            <p style={statLabel}>Snapshot</p>
            <p style={{ marginTop: "8px", fontWeight: 600, color: "#00293A" }}>
              {activeView === views.overall
                ? "Overall session"
                : cycle?.cycle_name ?? "Latest closed cycle"}
            </p>
          </div>
          <div style={card}>
            <p style={statLabel}>Score Basis</p>
            <p style={{ marginTop: "8px", fontWeight: 600, color: "#00293A" }}>
              Rating scaled by engagement
            </p>
          </div>
        </div>
      )}

      {error ? (
        <div style={{ marginBottom: "20px" }}>
          <StatusBanner tone="amber">{error}</StatusBanner>
        </div>
      ) : null}

      {isLoading ? (
        <div
          style={{
            borderRadius: "16px",
            border: "1px solid #dfe8ee",
            background: "#fff",
            padding: "24px",
            fontSize: "14px",
            color: "#475569",
            boxShadow: "0 16px 35px rgba(36,55,70,0.08)",
          }}
        >
          Loading leaderboard snapshots...
        </div>
      ) : (
        <LeaderboardTable rows={rows} />
      )}
    </PageShell>
  );
}
